import { cleanPhoneNumber } from "./util";

export interface OrderInvoicePair {
  orderNumber: string;
  invoiceNumber: string | null;
}

export interface DestinationInfo {
  customerName?: string;
  customerPhone?: string;
  fullAddress?: string;
  destinationAddress?: string;
  deliveryInstructions?: string;
  shipmentId?: string;
  orderNumbers?: string[];
  invoiceNumbers?: string[];
  orderInvoicePairs?: OrderInvoicePair[];
  vehicleType?: string;
  sourceLat?: number;
  sourceLng?: number;
  sourceName?: string;
  sourceAddress?: string;
  sourceContactName?: string;
  sourceContactPhone?: string;
}

const SEPARATOR_LINE = "─".repeat(30);
const GOOGLE_MAPS_BASE_URL = "https://maps.google.com/?q=";
const WHATSAPP_BASE_URL = "https://wa.me/";
const INDIA_COUNTRY_CODE = "91";

const VEHICLE_TYPE_MAP: Record<string, string> = {
  TWO_WHEELER: "2W",
  THREE_WHEELER: "3W",
  FOUR_WHEELER: "4W",
} as const;

function formatVehicleType(vehicleType: string): string {
  const upperType = vehicleType.toUpperCase();

  if (upperType in VEHICLE_TYPE_MAP) {
    return VEHICLE_TYPE_MAP[upperType];
  }

  for (const [key, value] of Object.entries(VEHICLE_TYPE_MAP)) {
    if (upperType.includes(key)) {
      return value;
    }
  }

  return vehicleType;
}

function extractInvoiceNumbers(destinationInfo: DestinationInfo): string[] {
  if (destinationInfo.orderInvoicePairs?.length) {
    return destinationInfo.orderInvoicePairs
      .map((pair) => pair.invoiceNumber)
      .filter((inv): inv is string => inv !== null);
  }

  return (
    destinationInfo.invoiceNumbers?.filter(
      (inv): inv is string => inv !== null && inv !== undefined
    ) || []
  );
}

function buildGoogleMapsLink(lat: number, lng: number): string {
  return `${GOOGLE_MAPS_BASE_URL}${lat},${lng}`;
}

function addLocationSection(
  messageParts: string[],
  title: string,
  contactName?: string,
  address?: string,
  lat?: number,
  lng?: number,
  contactPhone?: string
): void {
  messageParts.push(`> *${title}*`);
  messageParts.push("");

  if (contactName) {
    messageParts.push(`   >> *Contact Name:* ${contactName}`);
  }

  if (address) {
    messageParts.push(`   >> *Address:* ${address}`);
  }

  if (lat !== undefined && lng !== undefined) {
    messageParts.push(`   >> *Coordinates:* ${lat}, ${lng}`);
    messageParts.push(`   >> Google Maps: ${buildGoogleMapsLink(lat, lng)}`);
  }

  if (contactPhone) {
    messageParts.push(`   >> *Contact Phone:* ${contactPhone}`);
  }

  messageParts.push("");
}

export function generateLocationMessage(
  destLat: number,
  destLng: number,
  destinationInfo: DestinationInfo = {}
): string {
  const hasSource = Boolean(
    destinationInfo.sourceLat && destinationInfo.sourceLng
  );

  const messageParts: string[] = [];

  messageParts.push("*DELIVERY DETAILS*");
  messageParts.push("");

  if (hasSource) {
    addLocationSection(
      messageParts,
      "PICKUP LOCATION",
      destinationInfo.sourceContactName,
      destinationInfo.sourceAddress,
      destinationInfo.sourceLat,
      destinationInfo.sourceLng,
      destinationInfo.sourceContactPhone
    );
    messageParts.push(SEPARATOR_LINE);
    messageParts.push("");
  }

  const dropAddress =
    destinationInfo.fullAddress || destinationInfo.destinationAddress;
  addLocationSection(
    messageParts,
    "DROP LOCATION",
    destinationInfo.customerName,
    dropAddress,
    destLat,
    destLng,
    destinationInfo.customerPhone
  );

  const invoiceNumbers = extractInvoiceNumbers(destinationInfo);
  if (invoiceNumbers.length > 0) {
    messageParts.push("> *Garaaz Invoice Numbers:*");
    invoiceNumbers.forEach((invoiceNum, index) => {
      messageParts.push(`  ${index + 1}. ${invoiceNum}`);
    });
    messageParts.push("");
  }

  messageParts.push(SEPARATOR_LINE);
  messageParts.push("");

  if (destinationInfo.vehicleType) {
    const formattedVehicleType = formatVehicleType(destinationInfo.vehicleType);
    messageParts.push(`> *Vehicle Type:* ${formattedVehicleType}`);
    messageParts.push("");
  }

  if (
    destinationInfo.deliveryInstructions &&
    destinationInfo.deliveryInstructions.trim() !== "-"
  ) {
    messageParts.push("> *DELIVERY INSTRUCTIONS*");
    messageParts.push(`   ${destinationInfo.deliveryInstructions}`);
    messageParts.push("");
  }

  if (destinationInfo.shipmentId) {
    messageParts.push(SEPARATOR_LINE);
    messageParts.push(`> *Shipment ID:* ${destinationInfo.shipmentId}`);
  }

  return messageParts.join("\n");
}

export function openWhatsAppWithLocation(
  recipientPhone: string,
  destLat: number,
  destLng: number,
  destinationInfo: DestinationInfo = {}
): void {
  const cleanedDigits = cleanPhoneNumber(recipientPhone);

  if (!cleanedDigits || cleanedDigits.length !== 10) {
    throw new Error("Invalid phone number");
  }

  const cleanPhone = `${INDIA_COUNTRY_CODE}${cleanedDigits}`;
  const message = generateLocationMessage(destLat, destLng, destinationInfo);
  const encoded = encodeURIComponent(message);
  const waUrl = `${WHATSAPP_BASE_URL}${cleanPhone}?text=${encoded}`;

  window.open(waUrl, "_blank");
}

export function formatAddress(address: {
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  state: string;
  pincode: string;
  country?: string;
}): string {
  const parts: string[] = [];

  if (address.streetAddress1) {
    parts.push(address.streetAddress1);
  }
  if (address.streetAddress2) {
    parts.push(address.streetAddress2);
  }
  if (address.city) {
    parts.push(address.city);
  }
  if (address.state) {
    parts.push(address.state);
  }
  if (address.pincode) {
    parts.push(address.pincode);
  }
  if (address.country) {
    parts.push(address.country);
  }

  return parts.join(", ");
}
